import React from 'react';
import { FiMic, FiSquare, FiLoader } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const PushToTalkButton = ({ 
  callStatus, // 'idle', 'connecting', 'active'
  onStart, 
  onStop, 
  width = 240,
  height = 80,
  className = '',
  disabled = false 
}) => {
  
  const getVisualStatus = () => {
    if (callStatus === 'connecting') return 'connecting';
    if (callStatus === 'active') return 'recording';
    return 'idle';
  };

  const status = getVisualStatus();

  const handleToggle = () => {
    if (disabled) return;
    
    if (status === 'recording') {
      onStop?.();
    } else if (status === 'idle') {
      onStart?.();
    }
  };

  const getButtonStyles = () => {
    switch (status) {
      case 'connecting':
        return {
          bg: 'bg-gradient-to-b from-yellow-300 to-yellow-500',
          border: 'border-yellow-600',
          shadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.3)',
          ring: 'focus:ring-yellow-200',
          iconBg: 'bg-yellow-400/40'
        };
      case 'recording':
        return {
          bg: 'bg-gradient-to-b from-red-400 to-red-600',
          border: 'border-red-700',
          shadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.3)',
          ring: 'focus:ring-red-300',
          iconBg: 'bg-red-500/40'
        };
      default:
        return {
          bg: 'bg-gradient-to-b from-green-400 to-green-600',
          border: 'border-green-700',
          shadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.3)',
          ring: 'focus:ring-green-300',
          iconBg: 'bg-green-500/40'
        };
    }
  };

  const styles = getButtonStyles();

  return (
    <div className="relative" style={{ width: typeof width === 'number' ? `${width}px` : width, height: typeof height === 'number' ? `${height}px` : height }}>
      <button
        onClick={handleToggle}
        disabled={disabled || status === 'connecting'}
        className={`
          relative
          w-full h-full
          rounded-2xl
          transition-all
          duration-300
          ease-in-out
          transform
          active:scale-95
          focus:outline-none
          focus:ring-4
          focus:ring-opacity-50
          flex items-center justify-center
          ${styles.bg}
          ${styles.ring}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.02] cursor-pointer'}
          shadow-2xl
          border-b-4
          ${styles.border}
          ${className}
        `}
        style={{ boxShadow: styles.shadow }}
      >
        {/* Inner highlight for 3D effect */}
        <div className="absolute inset-x-0 top-0 h-1/2 rounded-t-2xl opacity-30 bg-gradient-to-b from-white to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 flex items-center justify-center space-x-2 sm:space-x-4 text-white">
          <div className={`p-1.5 sm:p-2.5 rounded-xl ${styles.iconBg} backdrop-blur-sm transition-colors duration-300`}>
            {status === 'connecting' ? (
              <SafeIcon icon={FiLoader} className="text-lg sm:text-2xl animate-spin" />
            ) : (
              <SafeIcon icon={status === 'recording' ? FiSquare : FiMic} className="text-lg sm:text-2xl" />
            )}
          </div>
          <span className="text-sm sm:text-2xl font-bold tracking-widest uppercase truncate px-1 sm:px-2">
            {status === 'idle' && 'Talk'}
            {status === 'connecting' && 'Connecting'}
            {status === 'recording' && 'Stop'}
          </span>
        </div>
      </button>
    </div>
  );
};

export default PushToTalkButton;
